import { useEffect, useMemo } from "react";

import { isEmpty } from "lodash/fp";

import { parseReactions } from "@app/analytics/api/models";
import useTranscriptStore from "@app/analytics/MeetingReport/stores/transcript";
import { getReactionsByParagraphId } from "@app/analytics/MeetingReport/utils";
import type {
  ReactionHighlight,
  TranscriptTurnParagraph,
} from "@app/common/types";

import useHighlightsQuery from "./useHighlightsQuery";

const REACTION_TYPES = ["positive", "negative"];
const EMPTY_REACTIONS: ReactionHighlight[] = [];

/**
 * This hook handles fetching highlights and transforming them into
 * transcript reactions, and will populate the transcript store with
 * a map of turn_id -> ReactionHighlight[]
 * */
const useTranscriptReactions = (turns: TranscriptTurnParagraph[]) => {
  const setReactionsByParagraphId = useTranscriptStore(
    (state) => state.setReactionsByParagraphId
  );

  const { data: highlights, isLoading: highlightsLoading } =
    useHighlightsQuery();

  const parsedReactions = useMemo(() => {
    if (isEmpty(highlights) || isEmpty(turns)) return EMPTY_REACTIONS;

    const reactionsFromHighlights = (highlights ?? []).filter((h) =>
      REACTION_TYPES.includes(h.type)
    );

    return parseReactions(reactionsFromHighlights, turns);
  }, [turns, highlights]);

  useEffect(() => {
    // populate the reactions in the store
    setReactionsByParagraphId(getReactionsByParagraphId(parsedReactions));
  }, [parsedReactions]);

  // include return value so it's backwards compatible w/ TranscriptTurns component
  return {
    isLoading: highlightsLoading,
    reactions: parsedReactions,
  };
};

export default useTranscriptReactions;
